<div class="flex flex-col gap-5">
    <div class="rounded-xl border border-border bg-card p-3.5 shadow-sm">
        @for (piece of editor().pieces; track $index; let index = $index; let first = $first) {
        <div [class]="pieceRowClass(first)">
            <div class="flex items-center gap-2">
                <p class="m-0 grid size-6 shrink-0 place-items-center rounded-md bg-brand-studio font-heading text-sm font-extrabold text-white"
                    aria-hidden="true">{{ pieceLetter(index) }}</p>
                <input [value]="piece.label" (input)="onPieceLabel(index, $event)" aria-label="Piece name"
                    class="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 py-0.5 font-heading text-lg font-extrabold text-foreground outline-none hover:border-border focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25" />
                @if (pieceCount() > 1) {
                <button type="button"
                    class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    [attr.aria-label]="'Remove video ' + pieceLetter(index)" (click)="removePiece(index)">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4"
                        aria-hidden="true">
                        <path d="M3 6h18" />
                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    </svg>
                </button>
                }
            </div>
            <ng-container *ngTemplateOutlet="placementControls; context: { index: index }" />
            <ng-container *ngTemplateOutlet="copyDisclosure; context: { index: index, shared: false }" />
            <ng-container *ngTemplateOutlet="pieceShape; context: { index: index, named: false }" />
            @if (pieceNeedsPlacement(index)) {
            <p class="m-0 text-xs font-semibold text-destructive">Pick at least one placement, or UGC only</p>
            }
        </div>
        }
    </div>

    <button type="button" class="btn-shout btn-shout-sm btn-shout-secondary self-end disabled:cursor-not-allowed disabled:opacity-40"
        [disabled]="pieceCount() >= 4" (click)="addPiece()">Add another piece</button>

    <div class="flex flex-col gap-2 rounded-xl bg-muted px-4 py-3.5">
        <p class="m-0 text-xs font-bold uppercase tracking-widest text-muted-foreground">What creators deliver</p>
        <p class="m-0 font-heading text-lg font-extrabold text-foreground">{{ pieceCount() }} unique {{ pieceWord() }}, {{ postCount() }} {{ postWord() }} in total</p>
        @if (showPrice() && quoteLabel(); as quote) {
        <p class="m-0 text-sm text-muted-foreground">{{ quote }} per creator</p>
        }
        <ul class="m-0 flex list-none flex-col gap-2 p-0">
            @for (piece of editor().pieces; track $index; let index = $index) {
            <li class="flex flex-wrap items-center gap-1.5 text-sm">
                <span class="min-w-16 font-bold text-foreground">{{ piece.label }}</span>
                @if (pieceIsUgc(index)) {
                <span class="rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-semibold text-foreground">UGC only</span>
                } @else if (channelsOn(index).length) {
                @for (channel of channelsOn(index); track channel) {
                <span class="rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-semibold text-foreground">{{ channelLabel(channel) }}</span>
                }
                } @else {
                <em class="text-sm font-normal not-italic text-muted-foreground">No placement yet</em>
                }
            </li>
            }
        </ul>
    </div>

    <div class="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3">
        <div class="min-w-0 flex-1">
            <p class="m-0 text-sm font-semibold text-foreground">Add a 12 month Advertising License for each video?</p>
            <div class="mt-1 text-sm text-muted-foreground">
                @if (advertisingLicenseLocked()) {
                <p class="m-0">Included once for each posted video.</p>
                } @else if (advertisingLicenseCostLabel(); as cost) {
                <p class="m-0">Optional. {{ cost }} per video.</p>
                } @else {
                <p class="m-0">Optional. Cost based on your Ad Content pricing.</p>
                }
            </div>
        </div>
        <bb-switch class="shrink-0" [checked]="advertisingLicenseLocked() || service.selectedAdvertisingOnly()"
            [disabled]="advertisingLicenseLocked()" ariaLabel="Add 12 month Advertising License for each video"
            (checkedChange)="onAdvertisingLicenseChange($event)" />
    </div>
</div>

<ng-template #placementControls let-index="index">
    <div class="flex flex-wrap items-center gap-1.5">
        <button type="button" [class]="ugcPillClass(hasUgc(index))" [attr.aria-pressed]="hasUgc(index)"
            title="Creators send you the file to use in your own ads. Nothing gets posted." (click)="toggleUgc(index)">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4" aria-hidden="true">
                <rect width="18" height="18" x="3" y="3" rx="2" />
                <path d="M7 3v18" />
                <path d="M3 7.5h4" />
                <path d="M3 12h18" />
                <path d="M3 16.5h4" />
                <path d="M17 3v18" />
                <path d="M17 7.5h4" />
                <path d="M17 16.5h4" />
            </svg>
            UGC only
        </button>
        <span class="mx-0.5 h-5 w-px bg-border" aria-hidden="true"></span>
        @for (group of placementGroups; track group.platform) {
        <span class="inline-flex h-7 items-stretch overflow-hidden rounded-lg border border-border"
            [attr.title]="group.platform">
            <span class="grid place-items-center border-r border-border bg-muted px-1.5">
                <img [src]="groupIcon(group.channelIds)" alt="" class="size-4 object-contain" />
            </span>
            @for (channel of channelsIn(group.channelIds); track channel.channelId; let first = $first) {
            <button type="button" [class]="segmentButtonClass(hasChannel(index, channel.channelId), first)"
                [attr.aria-pressed]="hasChannel(index, channel.channelId)"
                [attr.aria-label]="placementAria(group.platform, channel)"
                (click)="toggleChannel(index, channel.channelId)">{{ placementButtonLabel(channel) }}</button>
            }
        </span>
        }
    </div>
</ng-template>

<ng-template #copyDisclosure let-index="index" let-shared="shared">
    @if (showsCopy(index, shared)) {
    <button type="button"
        class="flex w-full min-w-0 items-center gap-2 border-t border-border py-2 text-left hover:text-brand-studio"
        [attr.aria-expanded]="copyExpanded(index, shared)" (click)="toggleCopy(index, shared)">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" [class]="chevronClass(copyExpanded(index, shared))"
            aria-hidden="true">
            <path d="m9 18 6-6-6-6" />
        </svg>
        <span class="shrink-0 text-sm font-bold text-foreground">{{ copyDisclosureLabel(index, shared) }}</span>
        <em class="min-w-0 truncate text-xs font-medium not-italic text-muted-foreground">{{ copyPreviewFor(index, shared)
            }}</em>
    </button>
    @if (copyExpanded(index, shared)) {
    <ng-container *ngTemplateOutlet="copyFields; context: { index: index, shared: shared }" />
    }
    }
</ng-template>

<ng-template #copyFields let-index="index" let-shared="shared">
    @if (shared) {
    <div class="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <label [class]="copyFieldClass() + ' sm:col-span-2'">
            Caption
            <input [class]="copyInputClass()" [value]="editor().sharedCopy.caption" (input)="onSharedCaption($event)"
                placeholder="e.g. My morning routine with @glowco #ad" />
        </label>
        <label [class]="copyFieldClass()">
            Call to action
            <input [class]="copyInputClass()" [value]="editor().sharedCopy.callToActionLink"
                (input)="onSharedLink($event)" placeholder="e.g. Shop now with code GLOW15" />
        </label>
        <label [class]="copyFieldClass()">
            Hashtags
            <input [class]="copyInputClass()" [value]="editor().sharedCopy.hashtags" (input)="onSharedHashtags($event)"
                placeholder="#glowco #ad" />
        </label>
    </div>
    } @else {
    @for (channel of channelsOn(index); track channel) {
    @if (showsChannelHeading(index)) {
    <p class="m-0 text-xs font-semibold text-foreground">{{ channelLabel(channel) }}</p>
    }
    <div class="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <label [class]="copyFieldClass() + ' sm:col-span-2'">
            Caption
            <input [class]="copyInputClass()" [value]="copyFor(index, channel).caption"
                (input)="onPlacementCaption(index, channel, $event)"
                placeholder="e.g. My morning routine with @glowco #ad" />
        </label>
        <label [class]="copyFieldClass()">
            Call to action
            <input [class]="copyInputClass()" [value]="copyFor(index, channel).callToActionLink"
                (input)="onPlacementLink(index, channel, $event)" placeholder="e.g. Shop now with code GLOW15" />
        </label>
        <label [class]="copyFieldClass()">
            Hashtags
            <input [class]="copyInputClass()" [value]="copyFor(index, channel).hashtags"
                (input)="onPlacementHashtags(index, channel, $event)" placeholder="#glowco #ad" />
        </label>
    </div>
    }
    }
</ng-template>

<ng-template #pieceShape let-index="index" let-named="named">
    <div class="flex flex-wrap items-center gap-1.5">
        @if (named) {
        <span class="text-xs font-semibold text-muted-foreground">{{ pieceLetter(index) }}</span>
        }
        @if (pieceIsUgc(index)) {
        <span class="text-xs font-semibold text-muted-foreground">Format</span>
        <span class="inline-flex h-7 items-stretch overflow-hidden rounded-lg border border-border">
            @for (format of videoFormats; track format.id; let first = $first) {
            <button type="button"
                [class]="segmentButtonClass(editor().pieces[index].format === formatFor(format.id), first)"
                [attr.aria-pressed]="editor().pieces[index].format === formatFor(format.id)"
                [attr.aria-label]="'Format ' + format.description"
                (click)="setPieceFormat(index, formatFor(format.id))">{{ format.description }}</button>
            }
        </span>
        }
        <span class="text-xs font-semibold text-muted-foreground" [class.ml-8]="pieceIsUgc(index)">Duration</span>
        <span class="inline-flex h-7 items-stretch overflow-hidden rounded-lg border border-border">
            @for (duration of videoDurations; track duration.id; let first = $first) {
            <button type="button"
                [class]="segmentButtonClass(editor().pieces[index].durationSeconds === durationFor(duration.id), first)"
                [attr.aria-pressed]="editor().pieces[index].durationSeconds === durationFor(duration.id)"
                [attr.aria-label]="duration.name" (click)="setPieceDuration(index, durationFor(duration.id))">{{
                durationLabel(duration.id) }}</button>
            }
        </span>
    </div>
</ng-template>